/**
 * A/B-test landing page variant, served at ``/beta``.
 *
 * Ported from a Claude Design drop ("Kleiber Site.html") — warm
 * editorial palette with Geist + Instrument Serif italic accents, a
 * terracotta accent hue, and an element-genome chip language. The
 * original drop was a multi-page React prototype; this variant
 * flattens the Home + selected Features/Safety/Pricing sections onto
 * one page so every non-signup interaction is a page-anchor scroll.
 *
 * Contract with the rest of the app:
 * - Every tailwind class / CSS variable in this file is scoped under
 *   ``.kleiber-b-landing`` (see ``landing-scientific.css``) so the
 *   warm palette cannot leak into ``/`` or ``/dashboard``.
 * - The beta-signup form posts to the existing ``/api/beta-signup``
 *   endpoint, same contract as the original ``LandingRoute`` — one
 *   funnel, two landing variants.
 * - Every other link on this page resolves to an on-page ``#anchor``;
 *   no hash-routing, no cross-page jumps. A/B test clarity: whichever
 *   visit came in to ``/b`` stays on ``/b``.
 *
 * Copy audit (landed against the codebase on 2026-04-19):
 * - Removed the "118 ad accounts testing" social-proof line —
 *   lifetime signups were 1 when this shipped, so the claim is false.
 * - Removed the "Bonferroni correction" chip from the stats section
 *   — ``src/services/stats.py`` does not implement multi-test
 *   correction today.
 * - Replaced the speculative $49 / $129 pricing tiers with a
 *   "free during private beta" framing — no pricing has been
 *   committed yet.
 * - Dropped "14-day free trial" references — not a committed SKU.
 * Everything else (z-test, Thompson sampling, min-N 1000, fatigue
 * detection, Fernet, 6am CT cron, 6-step cycle, 5-campaign cap,
 * gene-pool-gated LLM) matches ``src/`` and ``CLAUDE.md`` verbatim.
 */

import { useEffect, useState, type FormEvent } from "react";

import { api } from "@/lib/api/client";
import { captureUserSignup, trackSignupEvent } from "@/lib/analytics";

import "./landing-scientific.css";

/* ---------------------------------------------------------------- */
/* Page root                                                         */
/* ---------------------------------------------------------------- */

export function LandingScientificRoute() {
  useEffect(() => {
    const prev = document.title;
    document.title = "Kleiber — Autonomous ad-testing for Meta";
    return () => {
      document.title = prev;
    };
  }, []);

  return (
    <div className="kleiber-b-landing">
      <Nav />
      <main>
        <Hero />
        <TrustStrip />
        <ProblemSection />
        <FeatureReel />
        <StatsLanguageSection />
        <HowItWorksPreview />
        <SafetySection />
        <PricingSection />
        <SignupSection />
        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
}

/* ---------------------------------------------------------------- */
/* Nav                                                               */
/* ---------------------------------------------------------------- */

function Nav() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <nav
      style={{
        position: "sticky",
        top: 0,
        zIndex: 40,
        background: scrolled ? "oklch(98.5% 0.006 80 / 0.85)" : "transparent",
        backdropFilter: scrolled ? "blur(10px) saturate(1.2)" : "none",
        WebkitBackdropFilter: scrolled ? "blur(10px) saturate(1.2)" : "none",
        borderBottom: scrolled
          ? "1px solid var(--border-soft)"
          : "1px solid transparent",
        transition: "all .2s",
      }}
    >
      <div
        className="b-wrap"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: 68,
        }}
      >
        <a href="#top" style={{ display: "flex", alignItems: "center" }}>
          <Wordmark />
        </a>
        <div
          style={{ display: "flex", alignItems: "center", gap: 24 }}
          className="b-nav-links"
        >
          <div className="b-nav-anchors" style={{ display: "contents" }}>
            <NavAnchor href="#how-it-works">How it works</NavAnchor>
            <NavAnchor href="#safety">Safety</NavAnchor>
            <NavAnchor href="#pricing">Pricing</NavAnchor>
          </div>
          <a
            href="#signup"
            className="b-btn b-btn-primary b-btn-sm"
            style={{ textDecoration: "none" }}
          >
            Request access
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path
                d="M3 6h6m-3-3 3 3-3 3"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </a>
        </div>
      </div>
    </nav>
  );
}

function NavAnchor({ href, children }: { href: string; children: string }) {
  return (
    <a
      href={href}
      style={{
        fontSize: 14,
        color: "var(--muted)",
        fontWeight: 400,
        padding: "6px 2px",
        transition: "color .15s",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.color = "var(--ink)")}
      onMouseLeave={(e) => (e.currentTarget.style.color = "var(--muted)")}
    >
      {children}
    </a>
  );
}

/* ---------------------------------------------------------------- */
/* Wordmark                                                          */
/*                                                                   */
/* Matches the brand used on ``/`` and ``/product`` — the wordmark   */
/* IS the logo; no separate icon glyph. DM Serif Display is already  */
/* loaded by ``index.html`` for the existing landing.                */
/* ---------------------------------------------------------------- */

function Wordmark({ size = 22 }: { size?: number }) {
  return (
    <span
      style={{
        fontFamily: "'DM Serif Display', serif",
        fontSize: size,
        letterSpacing: "-0.01em",
        color: "var(--ink)",
        lineHeight: 1,
      }}
      aria-label="Kleiber"
    >
      Kleiber
    </span>
  );
}

/* ---------------------------------------------------------------- */
/* Hero                                                              */
/* ---------------------------------------------------------------- */

function Hero() {
  return (
    <section
      id="top"
      style={{
        paddingTop: 56,
        paddingBottom: 60,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        className="b-wrap"
        style={{ textAlign: "center", position: "relative", zIndex: 2 }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "5px 13px",
            borderRadius: 99,
            border: "1px solid var(--border)",
            background: "var(--paper-2)",
            fontSize: 12.5,
            color: "var(--ink-2)",
            marginBottom: 28,
          }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: 99,
              background: "var(--win)",
            }}
          />
          <span
            className="b-mono"
            style={{ fontSize: 11, letterSpacing: "0.05em" }}
          >
            Private beta · by invitation
          </span>
        </div>
        <h1
          className="b-h-display"
          style={{ margin: "0 auto", maxWidth: 980, textWrap: "balance" }}
        >
          Your Meta ads,
          <br />
          running <span className="b-serif">the scientific method</span>
          <br />
          on themselves.
        </h1>
        <p
          className="b-h-sub"
          style={{
            marginTop: 22,
            maxWidth: 640,
            marginLeft: "auto",
            marginRight: "auto",
            textWrap: "pretty",
          }}
        >
          Every morning, a short email from Kleiber shows you what won,
          what got paused, and what new variants it&apos;s proposing next.
          You decide which moves run on their own and which wait for your
          one-click approval.
        </p>
        <div className="b-cta-row">
          <a
            href="#signup"
            className="b-btn b-btn-primary b-btn-lg"
            style={{ textDecoration: "none" }}
          >
            Request access
          </a>
          <a
            href="#how-it-works"
            className="b-btn b-btn-ghost b-btn-lg"
            style={{ textDecoration: "none" }}
          >
            See how it works
          </a>
        </div>
        <div
          className="b-mono"
          style={{ fontSize: 11.5, color: "var(--muted)", marginTop: 18 }}
        >
          Connects via Meta OAuth · you own your data
        </div>
      </div>

      {/* Big product shot */}
      <div
        className="b-wrap b-hero-mock"
        style={{ marginTop: 56, position: "relative" }}
      >
        <DashboardMock />
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- */
/* Dashboard mock — simplified port                                  */
/* ---------------------------------------------------------------- */

const VARIANTS = [
  {
    name: "V-0041",
    genome: ["AI-Powered Ad Care", "Babysit your ads", "IMG_673", "LEARN_MORE"],
    roas: "4.81×",
    z: "+3.42",
    sig: "win" as const,
    pct: 38,
  },
  {
    name: "V-0039",
    genome: ["Stop wasting ad spend", "Stats-driven testing", "IMG_714", "SIGN_UP"],
    roas: "4.02×",
    z: "+2.18",
    sig: "win" as const,
    pct: 28,
  },
  {
    name: "V-0037",
    genome: ["Always-on optimization", "Babysit your ads", "IMG_673", "GET_OFFER"],
    roas: "3.10×",
    z: "+0.41",
    sig: "flat" as const,
    pct: 18,
  },
  {
    name: "V-0035",
    genome: ["Meta ads, on autopilot", "Autonomous testing", "IMG_602", "LEARN_MORE"],
    roas: "2.24×",
    z: "−0.84",
    sig: "flat" as const,
    pct: 12,
  },
  {
    name: "V-0033",
    genome: ["Test ads while you sleep", "Hands-off growth", "IMG_511", "SHOP_NOW"],
    roas: "1.12×",
    z: "−2.91",
    sig: "lose" as const,
    pct: 4,
  },
];

function AppWindow({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        borderRadius: 14,
        overflow: "hidden",
        border: "1px solid var(--border)",
        background: "white",
        boxShadow:
          "0 30px 80px oklch(30% 0.02 60 / 0.12), 0 6px 18px oklch(30% 0.02 60 / 0.06)",
      }}
    >
      <div
        style={{
          height: 38,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 14px",
          background: "var(--paper-2)",
          borderBottom: "1px solid var(--border)",
        }}
      >
        <div style={{ display: "flex", gap: 6 }}>
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: 99,
              background: "oklch(87% 0.01 70)",
            }}
          />
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: 99,
              background: "oklch(87% 0.01 70)",
            }}
          />
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: 99,
              background: "oklch(87% 0.01 70)",
            }}
          />
        </div>
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            color: "var(--muted)",
          }}
        >
          {title}
        </div>
        <div style={{ width: 40 }} />
      </div>
      {children}
    </div>
  );
}

function DashboardMock() {
  return (
    <AppWindow title="">
      <div
        data-b-grid
        className="b-dash-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "220px 1fr",
          minHeight: 560,
        }}
      >
        <DashboardSidebar />
        <DashboardMain />
      </div>
    </AppWindow>
  );
}

function DashboardSidebar() {
  return (
    <div
      className="b-dash-sidebar"
      style={{
        background: "var(--paper-2)",
        borderRight: "1px solid var(--border)",
        padding: "16px 12px",
        fontSize: 13,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "6px 8px",
        }}
      >
        <Wordmark size={15} />
        <span
          style={{
            fontWeight: 500,
            letterSpacing: "-0.01em",
            color: "var(--muted)",
            fontSize: 13,
          }}
        >
          · Acme Co.
        </span>
      </div>
      <div style={{ height: 14 }} />
      <SideItem active>Campaigns</SideItem>
      <SideItem>Gene pool</SideItem>
      <SideItem badge={3}>Approvals</SideItem>
      <SideItem>Reports</SideItem>
      <SideItem>Integrations</SideItem>
      <div style={{ height: 20 }} />
      <div
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 10,
          color: "var(--muted)",
          padding: "4px 8px",
          letterSpacing: "0.08em",
        }}
      >
        CAMPAIGNS
      </div>
      <SideItem subtle active>
        ● summer-sale
      </SideItem>
      <SideItem subtle>● brand-awareness</SideItem>
      <SideItem subtle>● winter-drop</SideItem>
      <SideItem subtle>○ launch-ctrl</SideItem>
      <div style={{ marginTop: 20 }}>
        <div
          style={{
            padding: 12,
            borderRadius: 10,
            background: "white",
            border: "1px solid var(--border)",
            fontSize: 11.5,
            lineHeight: 1.4,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              marginBottom: 4,
            }}
          >
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: 99,
                background: "var(--win)",
              }}
            />
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 10,
                color: "var(--muted)",
              }}
            >
              NEXT CYCLE
            </span>
          </div>
          <div style={{ fontWeight: 500 }}>Tomorrow morning</div>
        </div>
      </div>
    </div>
  );
}

function SideItem({
  children,
  active,
  subtle,
  badge,
}: {
  children: React.ReactNode;
  active?: boolean;
  subtle?: boolean;
  badge?: number;
}) {
  return (
    <div
      style={{
        padding: "7px 10px",
        borderRadius: 7,
        background: active ? "white" : "transparent",
        border: active ? "1px solid var(--border)" : "1px solid transparent",
        color: subtle ? "var(--muted)" : "var(--ink-2)",
        fontSize: subtle ? 12.5 : 13,
        fontWeight: active && !subtle ? 500 : 400,
        display: "flex",
        alignItems: "center",
        gap: 6,
        marginBottom: 2,
      }}
    >
      {children}
      {badge ? (
        <span
          style={{
            marginLeft: "auto",
            background: "var(--accent)",
            color: "white",
            fontSize: 10,
            padding: "2px 6px",
            borderRadius: 99,
            fontFamily: "var(--font-mono)",
            fontWeight: 500,
          }}
        >
          {badge}
        </span>
      ) : null}
    </div>
  );
}

function DashboardMain() {
  return (
    <div style={{ padding: 26 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 18,
        }}
      >
        <div>
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 10.5,
              color: "var(--muted)",
              letterSpacing: "0.1em",
            }}
          >
            CAMPAIGN
          </div>
          <div
            style={{
              fontSize: 22,
              fontWeight: 500,
              letterSpacing: "-0.02em",
              marginTop: 2,
            }}
          >
            summer-sale
          </div>
          <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
            <span className="b-chip b-chip-win b-chip-dot">RUNNING</span>
            <span className="b-chip b-mono">12 variants active</span>
            <span className="b-chip b-mono">$847 spent · 30d</span>
          </div>
        </div>
      </div>

      <div
        className="b-stat-row"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 10,
          marginBottom: 18,
        }}
      >
        <Stat label="ROAS" value="3.42×" delta="+0.61" good />
        <Stat label="CPA" value="$14.20" delta="−$3.10" good />
        <Stat label="CTR" value="2.08%" delta="+0.42" good />
        <Stat label="SPEND" value="$847" delta="$28/day" />
      </div>

      <div
        className="b-variant-table"
        style={{
          border: "1px solid var(--border)",
          borderRadius: 12,
          overflow: "hidden",
        }}
      >
        <div
          className="b-variant-header"
          style={{
            display: "grid",
            gridTemplateColumns: "40px 1.6fr 1fr 1fr 90px 70px",
            padding: "9px 14px",
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            color: "var(--muted)",
            letterSpacing: "0.1em",
            background: "var(--paper-2)",
            borderBottom: "1px solid var(--border)",
          }}
        >
          <span />
          <span>VARIANT · GENOME</span>
          <span>ROAS</span>
          <span>CONFIDENCE</span>
          <span>STATUS</span>
          <span style={{ textAlign: "right" }}>BUDGET</span>
        </div>
        {VARIANTS.map((v, i) => (
          <VariantRow key={v.name} v={v} i={i} />
        ))}
        {VARIANTS.map((v, i) => (
          <VariantCardMobile key={`m-${v.name}`} v={v} i={i} />
        ))}
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  delta,
  good,
}: {
  label: string;
  value: string;
  delta: string;
  good?: boolean;
}) {
  return (
    <div
      style={{
        padding: 14,
        border: "1px solid var(--border)",
        borderRadius: 10,
        background: "white",
      }}
    >
      <div
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 10,
          color: "var(--muted)",
          letterSpacing: "0.1em",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 22,
          fontWeight: 500,
          letterSpacing: "-0.02em",
          marginTop: 3,
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 11,
          color: good ? "oklch(40% 0.14 145)" : "var(--muted)",
          marginTop: 2,
        }}
      >
        {delta}
      </div>
    </div>
  );
}

function VariantRow({ v, i }: { v: (typeof VARIANTS)[number]; i: number }) {
  return (
    <div
      className="b-variant-row"
      style={{
        display: "grid",
        gridTemplateColumns: "40px 1.6fr 1fr 1fr 90px 70px",
        padding: "12px 14px",
        alignItems: "center",
        borderBottom:
          i < VARIANTS.length - 1 ? "1px solid var(--border-soft)" : "none",
        fontSize: 13,
        background:
          v.sig === "win" && i === 0 ? "oklch(98% 0.02 145 / 0.5)" : "white",
      }}
    >
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 11,
          color: "var(--muted)",
        }}
      >
        #{i + 1}
      </span>
      <div>
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 11.5,
            fontWeight: 500,
          }}
        >
          {v.name}
        </div>
        <div style={{ display: "flex", gap: 3, marginTop: 4, flexWrap: "wrap" }}>
          {v.genome.map((g) => (
            <span
              key={g}
              style={{
                fontSize: 10.5,
                padding: "1.5px 6px",
                background: "var(--paper-2)",
                borderRadius: 4,
                fontFamily: "var(--font-mono)",
                color: "var(--ink-2)",
                border: "1px solid var(--border-soft)",
                maxWidth: 130,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {g}
            </span>
          ))}
        </div>
      </div>
      <div
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 13,
          fontWeight: 500,
        }}
      >
        {v.roas}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <ZBar z={v.z} sig={v.sig} />
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 11.5,
            color:
              v.sig === "win"
                ? "oklch(40% 0.14 145)"
                : v.sig === "lose"
                  ? "oklch(45% 0.16 28)"
                  : "var(--muted)",
          }}
        >
          {v.z}
        </span>
      </div>
      <div>
        {v.sig === "win" && (
          <span className="b-chip b-chip-win" style={{ fontSize: 10 }}>
            WINNING
          </span>
        )}
        {v.sig === "lose" && (
          <span className="b-chip b-chip-lose" style={{ fontSize: 10 }}>
            PAUSED
          </span>
        )}
        {v.sig === "flat" && (
          <span className="b-chip b-mono" style={{ fontSize: 10 }}>
            FLAT
          </span>
        )}
      </div>
      <div
        style={{
          textAlign: "right",
          fontFamily: "var(--font-mono)",
          fontSize: 12,
        }}
      >
        {v.pct}%
      </div>
    </div>
  );
}

/**
 * Mobile-native variant row, rendered as a card.
 *
 * The desktop ``VariantRow`` is a 6-column grid that relies on
 * ~520px of horizontal room — on a 375px viewport it either clips
 * or needs horizontal scroll, which makes the hero product-shot
 * feel broken. This component renders the same data as a stacked
 * card that fits the viewport cleanly.
 *
 * Display switching happens in CSS: ``.b-variant-row`` is hidden at
 * <900px, ``.b-variant-card-mobile`` is hidden at ≥900px. Both row
 * components are rendered on every screen so React never has to
 * unmount one to mount the other during a resize.
 */
function VariantCardMobile({
  v,
  i,
}: {
  v: (typeof VARIANTS)[number];
  i: number;
}) {
  const zColor =
    v.sig === "win"
      ? "oklch(40% 0.14 145)"
      : v.sig === "lose"
        ? "oklch(45% 0.16 28)"
        : "var(--muted)";
  return (
    <div
      className="b-variant-card-mobile"
      style={{
        padding: "14px 16px",
        borderBottom:
          i < VARIANTS.length - 1 ? "1px solid var(--border-soft)" : "none",
        background:
          v.sig === "win" && i === 0 ? "oklch(98% 0.02 145 / 0.5)" : "white",
      }}
    >
      {/* Top row: rank + name + status pill */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          marginBottom: 8,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              color: "var(--muted)",
            }}
          >
            #{i + 1}
          </span>
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 12,
              fontWeight: 500,
            }}
          >
            {v.name}
          </span>
        </div>
        {v.sig === "win" && (
          <span className="b-chip b-chip-win" style={{ fontSize: 10 }}>
            WINNING
          </span>
        )}
        {v.sig === "lose" && (
          <span className="b-chip b-chip-lose" style={{ fontSize: 10 }}>
            PAUSED
          </span>
        )}
        {v.sig === "flat" && (
          <span className="b-chip b-mono" style={{ fontSize: 10 }}>
            FLAT
          </span>
        )}
      </div>

      {/* Genome chips — full-width row */}
      <div
        style={{
          display: "flex",
          gap: 3,
          flexWrap: "wrap",
          marginBottom: 10,
        }}
      >
        {v.genome.map((g) => (
          <span
            key={g}
            style={{
              fontSize: 10.5,
              padding: "1.5px 6px",
              background: "var(--paper-2)",
              borderRadius: 4,
              fontFamily: "var(--font-mono)",
              color: "var(--ink-2)",
              border: "1px solid var(--border-soft)",
            }}
          >
            {g}
          </span>
        ))}
      </div>

      {/* Stats row: ROAS · confidence · budget */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          fontFamily: "var(--font-mono)",
          fontSize: 11,
          color: "var(--muted)",
        }}
      >
        <span>
          ROAS{" "}
          <span
            style={{
              color: "var(--ink)",
              fontSize: 12,
              fontWeight: 500,
            }}
          >
            {v.roas}
          </span>
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <ZBar z={v.z} sig={v.sig} />
          <span style={{ color: zColor, fontSize: 11 }}>{v.z}</span>
        </div>
        <span>
          <span
            style={{
              color: "var(--ink)",
              fontSize: 12,
              fontWeight: 500,
            }}
          >
            {v.pct}%
          </span>{" "}
          budget
        </span>
      </div>
    </div>
  );
}

function ZBar({ z, sig }: { z: string; sig: "win" | "lose" | "flat" }) {
  const val = parseFloat(z);
  const mag = Math.min(Math.abs(val) / 4, 1);
  const color =
    sig === "win"
      ? "oklch(58% 0.14 145)"
      : sig === "lose"
        ? "oklch(60% 0.16 28)"
        : "oklch(75% 0.01 70)";
  return (
    <div
      style={{
        width: 60,
        height: 6,
        background: "var(--paper-3)",
        borderRadius: 99,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          left: "50%",
          top: 0,
          bottom: 0,
          width: 1,
          background: "var(--border)",
        }}
      />
      <div
        style={{
          position: "absolute",
          top: 0,
          bottom: 0,
          left: val > 0 ? "50%" : `${50 - 50 * mag}%`,
          width: `${50 * mag}%`,
          background: color,
          borderRadius: 99,
        }}
      />
    </div>
  );
}

/* ---------------------------------------------------------------- */
/* Trust strip                                                       */
/* ---------------------------------------------------------------- */

function TrustStrip() {
  const items = [
    { t: "Meta Marketing API", s: "OAuth · read + write" },
    { t: "scipy", s: "statistical tests" },
    { t: "PostgreSQL · TSDB", s: "time-series metrics" },
    { t: "Fernet", s: "encrypted tokens" },
  ];
  return (
    <section
      style={{
        padding: "40px 0",
        borderTop: "1px solid var(--border)",
        borderBottom: "1px solid var(--border)",
        background: "var(--paper-2)",
      }}
    >
      <div
        className="b-wrap"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 30,
        }}
      >
        <div className="b-eyebrow" style={{ flexShrink: 0 }}>
          THE STACK UNDERNEATH
        </div>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 28,
            alignItems: "center",
          }}
        >
          {items.map((it) => (
            <div key={it.t} style={{ fontSize: 13.5 }}>
              <div style={{ fontWeight: 500 }}>{it.t}</div>
              <div
                className="b-mono"
                style={{ fontSize: 10.5, color: "var(--muted)" }}
              >
                {it.s}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- */
/* Problem section                                                   */
/* ---------------------------------------------------------------- */

function ProblemSection() {
  const items = [
    {
      t: "It's a lot to keep up with.",
      d: "A handful of ads means checking metrics, comparing variants, and deciding what to pause or push — every day. The work adds up fast, and the dashboard never really closes.",
    },
    {
      t: "Good calls need real data.",
      d: "A new ad that hasn't popped yet might be a dud — or might just need more runway. Without a real statistical test in the mix, it's easy to pause too early and kill a variant that would have worked.",
    },
    {
      t: "Winners are hard to time.",
      d: "A single strong day tempts you to scale up. But one day isn't a trend, and scaling on noise costs more than it gains. Waiting for the data to be sure takes patience you don't always have.",
    },
  ];
  return (
    <section id="problem" style={{ padding: "120px 0 80px" }}>
      <div className="b-wrap">
        <div className="b-eyebrow">THE HARD PART</div>
        <h2
          className="b-h-section"
          style={{ maxWidth: 880, marginTop: 12, textWrap: "balance" }}
        >
          Consistent testing finds great ads.{" "}
          <span className="b-serif">Kleiber makes it easy.</span>
        </h2>
        <div
          data-b-grid
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 28,
            marginTop: 56,
          }}
        >
          {items.map((it, i) => (
            <div key={it.t}>
              <div
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 11,
                  color: "var(--muted)",
                }}
              >
                0{i + 1}
              </div>
              <h3
                style={{
                  fontSize: 19,
                  fontWeight: 500,
                  letterSpacing: "-0.015em",
                  marginTop: 10,
                  lineHeight: 1.3,
                }}
              >
                {it.t}
              </h3>
              <p
                style={{
                  fontSize: 14.5,
                  color: "var(--muted)",
                  lineHeight: 1.6,
                  marginTop: 10,
                }}
              >
                {it.d}
              </p>
            </div>
          ))}
        </div>
        <p
          style={{
            maxWidth: 680,
            margin: "56px auto 0",
            fontSize: 17,
            lineHeight: 1.55,
            color: "var(--ink-2)",
            textAlign: "center",
            textWrap: "pretty",
          }}
        >
          Kleiber does the watching, the tallying, and the waiting for
          you. It runs a real statistical test on every variant each
          night and only acts when the data is there to back it up. You
          wake up to a short email with what happened and what&apos;s
          proposed next.
        </p>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- */
/* Feature reel                                                      */
/* ---------------------------------------------------------------- */

function FeatureReel() {
  const features = [
    {
      eyebrow: "THE DAILY CYCLE",
      title: (
        <>
          Six steps, <span className="b-serif">ready with your coffee</span>
        </>
      ),
      body: (
        <>
          Poll → Analyze → Act → Generate → Deploy → Report. Finished
          before you&apos;re at your desk. Your morning email has what
          Kleiber did on its own, what it&apos;s proposing next, and
          anything waiting on your sign-off.
        </>
      ),
      mock: <CycleDiagramMock />,
      side: "right" as const,
    },
    {
      eyebrow: "THE GENE POOL",
      title: (
        <>
          Every ad decomposes into{" "}
          <span className="b-serif">headline, body, image, CTA</span>
        </>
      ),
      body: (
        <>
          Kleiber tracks how each element performs across every variant that
          uses it. So when your CTR drops, you don&apos;t just know the ad is
          underperforming — you know your headline is the problem, and which
          one would do better.
        </>
      ),
      mock: <GenePoolMock />,
      side: "left" as const,
    },
    {
      eyebrow: "THE DAILY REPORT",
      title: (
        <>
          A morning email that actually{" "}
          <span className="b-serif">explains what happened</span>
        </>
      ),
      body: (
        <>
          Written in plain English. What moved, why it moved, and what Kleiber
          proposes next. One-click approve for anything destructive or
          high-spend.
        </>
      ),
      mock: <EmailReportMock />,
      side: "right" as const,
    },
  ];
  return (
    <section id="how-it-works" style={{ padding: "80px 0" }}>
      <div className="b-wrap">
        {features.map((f, i) => (
          <div
            key={i}
            data-b-grid
            className="b-feature-row"
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1.3fr",
              gap: 64,
              alignItems: "center",
              marginBottom: 140,
              direction: f.side === "left" ? "rtl" : "ltr",
            }}
          >
            <div style={{ direction: "ltr" }}>
              <div className="b-eyebrow">{f.eyebrow}</div>
              <h2
                className="b-h-section"
                style={{ marginTop: 12, textWrap: "balance" }}
              >
                {f.title}
              </h2>
              <p
                style={{
                  fontSize: 17,
                  color: "var(--muted)",
                  lineHeight: 1.6,
                  marginTop: 16,
                  textWrap: "pretty",
                  maxWidth: 460,
                }}
              >
                {f.body}
              </p>
            </div>
            <div style={{ direction: "ltr" }}>{f.mock}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function CycleDiagramMock() {
  const steps = [
    { k: "poll", t: "Poll", d: "Pull yesterday's numbers from Meta" },
    { k: "analyze", t: "Analyze", d: "Run the statistical tests" },
    { k: "act", t: "Act", d: "Pause losers, scale winners" },
    { k: "generate", t: "Generate", d: "Draft 1–3 new variants to try" },
    { k: "deploy", t: "Deploy", d: "Push them live in Ads Manager" },
    { k: "report", t: "Report", d: "Email you a summary" },
  ];
  return (
    <AppWindow title="daily cycle">
      <div style={{ padding: 28 }}>
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10.5,
            color: "var(--muted)",
            letterSpacing: "0.1em",
          }}
        >
          RUNS DAILY · AUTOMATIC
        </div>
        <div
          style={{
            fontSize: 20,
            fontWeight: 500,
            letterSpacing: "-0.02em",
            marginTop: 4,
            marginBottom: 18,
          }}
        >
          Six steps, fully automated
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {steps.map((s, i) => (
            <div
              key={s.k}
              style={{
                display: "grid",
                gridTemplateColumns: "28px 1fr auto",
                alignItems: "center",
                gap: 14,
                padding: "10px 14px",
                background: "var(--paper-2)",
                borderRadius: 10,
                border: "1px solid var(--border-soft)",
              }}
            >
              <div
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: 99,
                  background: "white",
                  border: "1px solid var(--border)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontFamily: "var(--font-mono)",
                  fontSize: 11,
                  fontWeight: 500,
                }}
              >
                {i + 1}
              </div>
              <div>
                <div style={{ fontWeight: 500, fontSize: 14 }}>{s.t}</div>
                <div style={{ fontSize: 12, color: "var(--muted)" }}>
                  {s.d}
                </div>
              </div>
              <span
                className="b-chip b-chip-win b-chip-dot"
                style={{ fontSize: 9.5 }}
              >
                DONE · {String(i * 3 + 2).padStart(2, "0")}s
              </span>
            </div>
          ))}
        </div>
      </div>
    </AppWindow>
  );
}

function GenePoolMock() {
  const headlines = [
    { t: "AI-Powered Ad Care", l: "+41%", positive: true },
    { t: "Stop wasting ad spend", l: "+28%", positive: true },
    { t: "Meta ads, on autopilot", l: "−4%", positive: false },
    { t: "Always-on optimization", l: "+12%", positive: true },
    { t: "Test ads while you sleep", l: "−18%", positive: false },
  ];
  return (
    <AppWindow title="gene pool · summer-sale">
      <div style={{ padding: 24 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 18,
          }}
        >
          <div>
            <div className="b-eyebrow">GENE POOL · HUMAN-APPROVED</div>
            <div style={{ fontSize: 18, fontWeight: 500, marginTop: 2 }}>
              34 elements · 7 slots
            </div>
          </div>
        </div>
        <div
          style={{
            border: "1px solid var(--border)",
            borderRadius: 10,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "8px 12px",
              background: "var(--paper-2)",
              fontFamily: "var(--font-mono)",
              fontSize: 10.5,
              letterSpacing: "0.1em",
              color: "var(--muted)",
              borderBottom: "1px solid var(--border)",
            }}
          >
            <span>HEADLINES</span>
            <span>5</span>
          </div>
          {headlines.map((h) => (
            <div
              key={h.t}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "10px 14px",
                borderBottom: "1px solid var(--border-soft)",
                fontSize: 13,
              }}
            >
              <span>{h.t}</span>
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 11.5,
                  color: h.positive
                    ? "oklch(40% 0.14 145)"
                    : "oklch(48% 0.16 28)",
                  fontWeight: 500,
                }}
              >
                {h.l}
              </span>
            </div>
          ))}
        </div>
      </div>
    </AppWindow>
  );
}

function EmailReportMock() {
  return (
    <AppWindow title="mail.kleiber.ai">
      <div style={{ padding: 28, background: "white" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            color: "var(--muted)",
            marginBottom: 4,
          }}
        >
          <span>From: kleiber &lt;reports@kleiber.ai&gt;</span>
          <span>Apr 18 · 06:14</span>
        </div>
        <h3
          style={{
            fontSize: 22,
            fontWeight: 500,
            letterSpacing: "-0.02em",
            margin: "8px 0 18px",
          }}
        >
          Your daily cycle:{" "}
          <span className="b-serif">3 wins, 2 pauses, 2 new variants</span>
        </h3>
        <div
          style={{ height: 1, background: "var(--border)", marginBottom: 20 }}
        />
        <div
          style={{ fontSize: 14, lineHeight: 1.6, color: "var(--ink-2)" }}
        >
          <p style={{ margin: 0 }}>
            Good morning. Since yesterday, summer-sale added <b>$28 in
            spend</b> and pulled a 3.42× ROAS — up from 2.81× last week.
          </p>
          <p>
            V-0041 (&quot;AI-Powered Ad Care&quot; with the hero image) is
            now clearly winning. Kleiber recommends scaling its budget
            from 20% → 38%.
          </p>
          <div
            style={{
              background: "var(--paper-2)",
              borderRadius: 10,
              padding: 14,
              marginTop: 16,
            }}
          >
            <div className="b-eyebrow" style={{ marginBottom: 8 }}>
              Waiting on your approval
            </div>
            <div
              style={{ display: "flex", flexDirection: "column", gap: 6 }}
            >
              <span
                className="b-chip b-mono"
                style={{ fontSize: 11, alignSelf: "flex-start" }}
              >
                Scale V-0041 · 20% → 38% of budget
              </span>
              <span
                className="b-chip b-mono"
                style={{ fontSize: 11, alignSelf: "flex-start" }}
              >
                V-0042 · headline swap → &quot;Stop wasting ad spend&quot;
              </span>
              <span
                className="b-chip b-mono"
                style={{ fontSize: 11, alignSelf: "flex-start" }}
              >
                V-0043 · image swap → IMG_714
              </span>
            </div>
          </div>
        </div>
      </div>
    </AppWindow>
  );
}

/* ---------------------------------------------------------------- */
/* Stats language (dark section)                                     */
/* ---------------------------------------------------------------- */

function StatsLanguageSection() {
  return (
    <section
      id="stats"
      style={{
        background: "var(--ink)",
        color: "var(--paper)",
        padding: "100px 0",
        margin: "40px 0",
      }}
    >
      <div className="b-wrap">
        <div
          data-b-grid
          className="b-stats-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 80,
            alignItems: "center",
          }}
        >
          <div>
            <div className="b-eyebrow" style={{ color: "oklch(65% 0.01 70)" }}>
              HOW IT DECIDES
            </div>
            <h2
              className="b-h-section"
              style={{ marginTop: 12, textWrap: "balance" }}
            >
              Every move has the math behind it.{" "}
              <span
                className="b-serif"
                style={{ color: "oklch(75% 0.1 55)" }}
              >
                Not a guess.
              </span>
            </h2>
            <p
              style={{
                fontSize: 17,
                color: "oklch(75% 0.01 70)",
                lineHeight: 1.6,
                marginTop: 18,
                maxWidth: 460,
              }}
            >
              Before Kleiber recommends scaling a winner or pausing a
              loser, it runs a proper statistical test on your campaign
              data and waits until there&apos;s enough signal — at least
              1,000 impressions — to be confident the result isn&apos;t
              random noise. Every call about your budget is decided by
              the numbers.
            </p>
            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                marginTop: 24,
              }}
            >
              {[
                "Real statistical tests",
                "Smart budget allocation",
                "Fatigue detection",
                "Minimum sample size",
              ].map(
                (t) => (
                  <span
                    key={t}
                    className="b-chip"
                    style={{
                      background: "oklch(25% 0.02 60)",
                      border: "1px solid oklch(32% 0.02 60)",
                      color: "oklch(85% 0.01 70)",
                    }}
                  >
                    {t}
                  </span>
                ),
              )}
            </div>
          </div>
          <div
            style={{
              background: "oklch(17% 0.02 60)",
              borderRadius: 14,
              padding: 28,
              fontFamily: "var(--font-mono)",
              fontSize: 12.5,
              lineHeight: 1.75,
              color: "oklch(80% 0.01 70)",
              border: "1px solid oklch(26% 0.02 60)",
            }}
          >
            <div style={{ color: "oklch(55% 0.01 70)" }}>
              # campaigns/summer-sale/cycle.py
            </div>
            <div>
              <span style={{ color: "oklch(70% 0.08 55)" }}>def</span>{" "}
              <span style={{ color: "white" }}>analyze</span>(variant,
              baseline):
            </div>
            <div style={{ paddingLeft: 16 }}>
              p1, n1 = variant.ctr, variant.impressions
              <br />
              p0, n0 = baseline.ctr, baseline.impressions
              <br />
              <span style={{ color: "oklch(55% 0.01 70)" }}>
                # real statistical test
              </span>
              <br />
              z, p_val ={" "}
              <span style={{ color: "oklch(80% 0.1 140)" }}>
                stats.proportions_ztest
              </span>
              (
              <br />
              <span style={{ paddingLeft: 16 }}>
                [p1*n1, p0*n0], [n1, n0]
              </span>
              <br />)
              <br />
              <span style={{ color: "oklch(70% 0.08 55)" }}>if</span> p_val
              &lt; <span style={{ color: "oklch(80% 0.14 55)" }}>0.05</span>{" "}
              <span style={{ color: "oklch(70% 0.08 55)" }}>and</span> n1 &gt;{" "}
              <span style={{ color: "oklch(80% 0.14 55)" }}>1000</span>:
              <br />
              <span style={{ paddingLeft: 16, color: "oklch(80% 0.1 140)" }}>
                scale_variant
              </span>
              (variant, z)
              <br />
              <span style={{ color: "oklch(70% 0.08 55)" }}>else</span>:
              <br />
              <span style={{ paddingLeft: 16, color: "oklch(55% 0.01 70)" }}>
                # not enough signal — hold
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- */
/* Weekly preview                                                    */
/* ---------------------------------------------------------------- */

function HowItWorksPreview() {
  const rows = [
    {
      day: "Mon",
      action:
        "3 variants running. One is pulling ahead on click-through — not by enough to act on yet.",
      delta: "$128 spent",
    },
    {
      day: "Tue",
      action:
        "Kleiber proposes a new variant — different body copy, same headline. Auto-approved under your rules.",
      delta: "+1 variant",
    },
    {
      day: "Wed",
      action:
        "Yesterday's winner holds up statistically. Scaled from 20% → 35% of budget.",
      delta: "ROAS 2.8× → 3.4×",
      good: true,
    },
    {
      day: "Thu",
      action:
        "Kleiber notices one variant's performance has been declining for 4 days. Paused before it burns more budget.",
      delta: "−$14/day savings",
      good: true,
    },
    {
      day: "Fri",
      action:
        "Kleiber notices one image is driving +14% across every variant using it.",
      delta: "new insight",
    },
    {
      day: "Sat",
      action:
        "Two new variants generated, both built around that winning image.",
      delta: "+2 variants",
    },
    {
      day: "Sun",
      action:
        "Weekly report: which variants won, which headlines pulled, which pairings work.",
      delta: "1 email",
    },
  ];
  return (
    <section style={{ padding: "100px 0" }}>
      <div className="b-wrap" style={{ textAlign: "center" }}>
        <div className="b-eyebrow">A TYPICAL WEEK</div>
        <h2
          className="b-h-section"
          style={{
            marginTop: 12,
            maxWidth: 760,
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          The winning combinations{" "}
          <span className="b-serif">compound.</span>
        </h2>
      </div>
      <div className="b-wrap" style={{ marginTop: 56 }}>
        <div
          style={{
            border: "1px solid var(--border)",
            borderRadius: 16,
            overflow: "hidden",
          }}
        >
          {rows.map((r, i) => (
            <div
              key={r.day}
              className="b-week-row"
              style={{
                display: "grid",
                gridTemplateColumns: "90px 1fr 200px",
                alignItems: "center",
                padding: "18px 24px",
                borderBottom:
                  i < rows.length - 1
                    ? "1px solid var(--border-soft)"
                    : "none",
                background: "white",
              }}
            >
              <div
                className="b-mono"
                style={{
                  fontSize: 12,
                  color: "var(--muted)",
                  letterSpacing: "0.08em",
                }}
              >
                {r.day.toUpperCase()}
              </div>
              <div style={{ fontSize: 15 }}>{r.action}</div>
              <div
                className="b-mono"
                style={{
                  fontSize: 12,
                  textAlign: "right",
                  color: r.good ? "oklch(40% 0.14 145)" : "var(--muted)",
                }}
              >
                {r.delta}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- */
/* Safety grid                                                       */
/* ---------------------------------------------------------------- */

function SafetySection() {
  const items = [
    { t: "Daily budget cap", d: "Per-campaign + per-variant. Kleiber physically cannot spend beyond it." },
    { t: "Approval queue", d: "Gate any destructive action — pauses, scale-ups, new launches — on a weekly review." },
    { t: "Gene pool is source of truth", d: "The LLM can only recombine elements you've approved. It never invents copy." },
    { t: "Encrypted at rest", d: "Meta tokens are Fernet-encrypted. Each account lives in its own tenant." },
    { t: "Pausable, always", d: "One click and everything freezes exactly where it is. No cleanup required." },
    { t: "Full audit log", d: "Every action — pause, scale, generate — is recorded with the z-score and sample size that triggered it." },
  ];
  return (
    <section id="safety" style={{ padding: "100px 0" }}>
      <div className="b-wrap">
        <div
          data-b-grid
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 2fr",
            gap: 64,
            alignItems: "start",
          }}
        >
          <div className="b-safety-sticky" style={{ position: "sticky", top: 100 }}>
            <div className="b-eyebrow">SAFETY & CONTROL</div>
            <h2 className="b-h-section" style={{ marginTop: 12 }}>
              Autonomous, <span className="b-serif">not out of hand.</span>
            </h2>
            <p
              style={{
                fontSize: 16,
                color: "var(--muted)",
                lineHeight: 1.6,
                marginTop: 16,
              }}
            >
              We built Kleiber for the person spending $100–$1K/mo on ads, not
              a hedge fund. Every move has a brake.
            </p>
          </div>
          <div
            data-b-grid
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 2,
              background: "var(--border)",
              borderRadius: 14,
              overflow: "hidden",
            }}
          >
            {items.map((it) => (
              <div
                key={it.t}
                style={{ background: "white", padding: 24 }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 10,
                  }}
                >
                  <span
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: 99,
                      background: "var(--accent)",
                    }}
                  />
                  <span style={{ fontSize: 15, fontWeight: 500 }}>{it.t}</span>
                </div>
                <p
                  style={{
                    fontSize: 13.5,
                    color: "var(--muted)",
                    margin: 0,
                    lineHeight: 1.55,
                  }}
                >
                  {it.d}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- */
/* Pricing                                                           */
/* ---------------------------------------------------------------- */

function PricingSection() {
  return (
    <section
      id="pricing"
      style={{
        padding: "80px 0",
        background: "var(--paper-2)",
        borderTop: "1px solid var(--border)",
        borderBottom: "1px solid var(--border)",
      }}
    >
      <div
        className="b-wrap"
        style={{ textAlign: "center", marginBottom: 48 }}
      >
        <div className="b-eyebrow">PRICING</div>
        <h2 className="b-h-section" style={{ marginTop: 12 }}>
          Built for the{" "}
          <span className="b-serif">$100–$1K/mo</span> spender.
        </h2>
        <p
          className="b-h-sub"
          style={{
            marginTop: 14,
            maxWidth: 620,
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          Join the private beta and lock in free access — for good. Paid
          tiers will come at public launch, but beta members keep theirs
          as long as they want it.
        </p>
      </div>
      <div
        className="b-wrap"
        style={{
          display: "grid",
          gridTemplateColumns: "1fr",
          gap: 18,
          maxWidth: 540,
          margin: "0 auto",
        }}
      >
        <div
          style={{
            background: "var(--ink)",
            color: "var(--paper)",
            border: "1px solid var(--ink)",
            borderRadius: 16,
            padding: 32,
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontSize: 13,
              fontWeight: 500,
              letterSpacing: "-0.01em",
              marginBottom: 18,
            }}
          >
            PRIVATE BETA
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              gap: 4,
              justifyContent: "center",
            }}
          >
            <span
              style={{
                fontSize: 48,
                fontWeight: 500,
                letterSpacing: "-0.03em",
              }}
            >
              Free
            </span>
          </div>
          <div
            style={{
              fontSize: 13,
              color: "oklch(75% 0.01 70)",
              marginTop: 6,
              fontFamily: "var(--font-mono)",
              letterSpacing: "0.02em",
            }}
          >
            for beta members, forever
          </div>
          <div
            style={{
              height: 1,
              background: "oklch(30% 0.02 60)",
              margin: "20px 0",
            }}
          />
          <ul
            style={{
              listStyle: "none",
              padding: 0,
              margin: 0,
              display: "flex",
              flexDirection: "column",
              gap: 10,
              textAlign: "left",
            }}
          >
            {[
              "Full daily cycle — all six steps",
              "Up to 5 campaigns per account",
              "Approval queue for every destructive action",
              "Daily + weekly email reports",
              "Element attribution + interaction tracking",
            ].map((p) => (
              <li
                key={p}
                style={{
                  fontSize: 14,
                  display: "flex",
                  gap: 10,
                  alignItems: "center",
                }}
              >
                <span
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: 99,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "oklch(30% 0.02 60)",
                    flexShrink: 0,
                  }}
                >
                  <svg width="8" height="8" viewBox="0 0 8 8">
                    <path
                      d="M1.5 4L3.5 6L6.5 2"
                      stroke="oklch(80% 0.1 140)"
                      strokeWidth="1.5"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                {p}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- */
/* Signup section                                                    */
/* ---------------------------------------------------------------- */

function SignupSection() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<
    "idle" | "submitting" | "success" | "error"
  >("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const normalized = email.trim().toLowerCase();
    // Fire attempt BEFORE client-side validation so the funnel can
    // distinguish "tried to submit but typed a bad email" from "never
    // tried at all". Variant tag on every event so PostHog can split
    // /beta vs / in a single funnel view.
    trackSignupEvent("beta_signup_submit_attempt", "scientific");
    if (!normalized || !normalized.includes("@")) {
      setErrorMsg("Please enter a valid email address.");
      setStatus("error");
      trackSignupEvent("beta_signup_error", "scientific", {
        reason: "client_validation_invalid_email",
      });
      return;
    }
    setStatus("submitting");
    try {
      await api.post("/api/beta-signup", { email: normalized });
      setStatus("success");
      trackSignupEvent("beta_signup_success", "scientific");
      // KLEIBER-6: identify + fire the unified user_signed_up event so
      // the PostHog Slack CDP function can alert #new-signups.
      captureUserSignup({ email: normalized, variant: "scientific" });
    } catch {
      setErrorMsg("Something went wrong. Please try again.");
      setStatus("error");
      trackSignupEvent("beta_signup_error", "scientific", {
        reason: "api_error",
      });
    }
  };

  return (
    <section id="signup" style={{ padding: "120px 0 40px" }}>
      <div className="b-wrap-narrow" style={{ textAlign: "center" }}>
        <div className="b-eyebrow">REQUEST ACCESS</div>
        <h2
          className="b-h-section"
          style={{
            marginTop: 12,
            maxWidth: 720,
            marginLeft: "auto",
            marginRight: "auto",
            textWrap: "balance",
          }}
        >
          Put me on the <span className="b-serif">waitlist.</span>
        </h2>
        <p
          className="b-h-sub"
          style={{
            marginTop: 14,
            maxWidth: 560,
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          We&apos;re onboarding a handful of advertisers at a time so we
          can shadow every cycle. Drop your email and we&apos;ll reach
          out within a few days — and you keep free access for good.
        </p>

        {status === "success" ? (
          <div
            style={{
              marginTop: 40,
              maxWidth: 420,
              marginLeft: "auto",
              marginRight: "auto",
              padding: 24,
              background: "var(--win-soft)",
              borderRadius: 14,
              border: "1px solid var(--border-soft)",
            }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 99,
                background: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 12px",
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path
                  d="M6 12l4 4 8-9"
                  stroke="oklch(42% 0.14 145)"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h3
              style={{
                fontSize: 19,
                fontWeight: 500,
                letterSpacing: "-0.02em",
                margin: "0 0 6px",
                color: "oklch(32% 0.14 145)",
              }}
            >
              You&apos;re on the list.
            </h3>
            <p
              style={{
                fontSize: 14,
                color: "var(--ink-2)",
                margin: 0,
              }}
            >
              We&apos;ll email <b>{email}</b> within a few days.
            </p>
          </div>
        ) : (
          <form
            onSubmit={onSubmit}
            noValidate
            style={{
              marginTop: 32,
              maxWidth: 440,
              marginLeft: "auto",
              marginRight: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            <input
              type="email"
              placeholder="you@company.co"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (status === "error") setStatus("idle");
              }}
              className="b-input"
              aria-invalid={status === "error"}
            />
            <button
              type="submit"
              className="b-btn b-btn-primary b-btn-lg"
              disabled={status === "submitting" || !email.includes("@")}
              style={{ width: "100%" }}
            >
              {status === "submitting" ? "Submitting…" : "Request access"}
            </button>
            {status === "error" && (
              <p
                role="alert"
                style={{
                  fontSize: 13,
                  color: "oklch(42% 0.16 28)",
                  margin: 0,
                }}
              >
                {errorMsg}
              </p>
            )}
            <div
              className="b-mono"
              style={{
                fontSize: 11.5,
                color: "var(--muted)",
                textAlign: "center",
                marginTop: 4,
              }}
            >
              · no credit card · connects via Meta OAuth ·
            </div>
          </form>
        )}
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- */
/* Final CTA                                                         */
/* ---------------------------------------------------------------- */

function FinalCTA() {
  return (
    <section style={{ padding: "80px 0 80px", textAlign: "center" }}>
      <div className="b-wrap-narrow">
        <h2 className="b-final-heading">
          Let the <span className="b-serif">math</span> do the digging.
          <br />
          You focus on <span className="b-serif">making</span> the ads.
        </h2>
        <div className="b-cta-row" style={{ marginTop: 36 }}>
          <a
            href="#signup"
            className="b-btn b-btn-primary b-btn-lg"
            style={{ textDecoration: "none" }}
          >
            Request access
          </a>
          <a
            href="#how-it-works"
            className="b-btn b-btn-ghost b-btn-lg"
            style={{ textDecoration: "none" }}
          >
            How it works
          </a>
        </div>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- */
/* Footer                                                            */
/* ---------------------------------------------------------------- */

function Footer() {
  return (
    <footer
      style={{
        borderTop: "1px solid var(--border)",
        marginTop: 120,
        paddingTop: 56,
        paddingBottom: 48,
        background: "var(--paper-2)",
      }}
    >
      <div
        className="b-wrap"
        data-b-grid
        style={{
          display: "grid",
          gridTemplateColumns: "1.4fr 1fr 1fr",
          gap: 48,
        }}
      >
        <div>
          <Wordmark size={24} />
          <p
            style={{
              marginTop: 16,
              color: "var(--muted)",
              fontSize: 14,
              maxWidth: 300,
              lineHeight: 1.55,
            }}
          >
            Autonomous ad-testing for Meta.
            <br />
            Runs the scientific method on your creative, every day.
          </p>
        </div>
        <div>
          <div className="b-eyebrow" style={{ marginBottom: 14 }}>
            Product
          </div>
          <FootLink href="#how-it-works">How it works</FootLink>
          <FootLink href="#safety">Safety</FootLink>
          <FootLink href="#pricing">Pricing</FootLink>
        </div>
        <div>
          <div className="b-eyebrow" style={{ marginBottom: 14 }}>
            Connect
          </div>
          <FootLink href="#signup">Request access</FootLink>
          <FootLink href="mailto:hi@kleiber.ai">hi@kleiber.ai</FootLink>
        </div>
      </div>
      <div
        className="b-wrap"
        style={{
          marginTop: 56,
          paddingTop: 20,
          borderTop: "1px solid var(--border)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontFamily: "var(--font-mono)",
          fontSize: 11.5,
          color: "var(--muted)",
        }}
      >
        <span>© 2026 Kleiber Labs — All rights reserved</span>
        <span>built in CT</span>
      </div>
    </footer>
  );
}

function FootLink({
  href,
  children,
}: {
  href: string;
  children: React.ReactNode;
}) {
  return (
    <a
      href={href}
      style={{
        display: "block",
        padding: "5px 0",
        color: "var(--ink-2)",
        fontSize: 14,
        transition: "color .15s",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.color = "var(--accent)")}
      onMouseLeave={(e) => (e.currentTarget.style.color = "var(--ink-2)")}
    >
      {children}
    </a>
  );
}
